import axios from 'axios';
import { logger } from '@/shared/logger';
import { ERROR_RESOLUTION } from './types';
import type { ResolvedApiError } from './types';

/** Statuses that are expected/user-actionable enough not to warrant a Sentry report. */
const SILENT_STATUSES = new Set([401, 403, 404, 409]);

/**
 * Error keys that must never report to Sentry regardless of status (ADR-7 re-decision).
 * `internal.validation.failed` (422): the frontend already validates client-side, so a
 * backend 422 here is a safety net, not an engineering signal, and would otherwise
 * generate steady volume from ordinary user input mistakes.
 */
const SILENT_KEYS: ReadonlySet<string> = new Set(['internal.validation.failed']);

/**
 * Sentry reporting policy. Priority order:
 *   1. ContractViolation — always report, overrides the status filter.
 *   2. ContractKeyFallback — always report; it flags a catalog gap (ADR-2) that must be
 *      closed, even though the user already sees real domain copy from the fallback.
 *   3. No `status` (network / timeout) — always report.
 *   4. `status >= 500` — always report.
 *   5. `key` in `SILENT_KEYS` — do NOT report. Checked AFTER the `>= 500` rule so a key
 *      can never silence a genuine server error.
 *   6. `status` in {401,403,404,409} — do NOT report.
 *   7. else (e.g. 400, 422, 429) — report.
 *
 * Accepted tradeoff (R9): for a reported 5xx, the backend's own `detail` is frequently a
 * literal `null` (the exact case `schema.ts` now parses instead of rejecting), so
 * `reportApiError`'s `detail` context field is permanently empty for exactly the errors
 * where it would matter most. This is backend-owned and environment-independent — no
 * frontend code can recover it. Not a bug; do not "fix" it here.
 */
export const shouldReport = (resolved: ResolvedApiError): boolean => {
	if (resolved.resolution === ERROR_RESOLUTION.ContractViolation) return true;
	if (resolved.resolution === ERROR_RESOLUTION.ContractKeyFallback) return true;
	if (resolved.status === undefined) return true;
	if (resolved.status >= 500) return true;
	if (typeof resolved.key === 'string' && SILENT_KEYS.has(resolved.key)) return false;
	return !SILENT_STATUSES.has(resolved.status);
};

/** Normalizes numeric path segments so the Sentry grouping-key title stays low-cardinality. */
const normalizePath = (path: string): string =>
	path
		.split('/')
		.map((segment) => (/^\d+$/.test(segment) ? ':id' : segment))
		.join('/');

const buildTitle = (error: unknown, resolved: ResolvedApiError): string => {
	const isAxios = axios.isAxiosError(error);
	const method = isAxios && error.config?.method ? error.config.method.toUpperCase() : '';
	const url = isAxios ? error.config?.url : undefined;

	const path = url ? normalizePath(url) : 'unknown';
	const status = resolved.status ?? resolved.resolution;
	return `API ${method} ${path} -> ${status}`.replace(/\s+/g, ' ').trim();
};

/**
 * Reports an API error to the logger (Sentry, once wired). Passes `error` itself as an
 * arg so `logger.error`'s `args.find(a => a instanceof Error)` (once implemented) can
 * pick it up for the stack trace. Raw `detail` goes in the context payload only — never
 * rendered to users.
 */
export const reportApiError = (error: unknown, resolved: ResolvedApiError): void => {
	const title = buildTitle(error, resolved);
	logger.error(title, error, {
		status: resolved.status,
		key: resolved.key,
		detail: resolved.detail,
		resolution: resolved.resolution,
	});
};
